// src/components/communications/SMSLogs.js
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Skeleton,
  TablePagination, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  Tabs, Tab, TextField, Button, Grid
} from '@mui/material';
import { Visibility as VisibilityIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import { communicationAPI } from '../../services/api';

const SMSLogs = () => {
  const [tabValue, setTabValue] = useState(0);
  const [emailLogs, setEmailLogs] = useState([]);
  const [smsLogs, setSmsLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [emailTotal, setEmailTotal] = useState(0);
  const [smsTotal, setSmsTotal] = useState(0);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedSms, setSelectedSms] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showSmsDialog, setShowSmsDialog] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    if (tabValue === 0) {
      fetchEmailLogs();
    } else {
      fetchSmsLogs();
    }
  }, [tabValue, page, rowsPerPage, dateFrom, dateTo]);

  const fetchEmailLogs = async () => {
    setLoading(true);
    try {
      const response = await communicationAPI.getEmailLogs({
        page: page + 1,
        per_page: rowsPerPage,
        date_from: dateFrom,
        date_to: dateTo
      });
      setEmailLogs(response.data.data || response.data);
      setEmailTotal(response.data.total || response.data.length);
    } catch (error) {
      console.error('Error fetching email logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSmsLogs = async () => {
    setLoading(true);
    try {
      const response = await communicationAPI.getSmsLogs({
        page: page + 1,
        per_page: rowsPerPage,
        date_from: dateFrom,
        date_to: dateTo
      });
      setSmsLogs(response.data.data || response.data);
      setSmsTotal(response.data.total || response.data.length);
    } catch (error) {
      console.error('Error fetching SMS logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewEmail = (email) => {
    setSelectedEmail(email);
    setShowEmailDialog(true);
  };

  const handleViewSms = (sms) => {
    setSelectedSms(sms);
    setShowSmsDialog(true);
  };

  const handleClearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  if (loading && emailLogs.length === 0 && smsLogs.length === 0) {
    return (
      <Box p={3}>
        <Skeleton variant="text" width={200} height={30} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>Communication Logs</Typography>
        <Typography variant="body1" color="text.secondary">
          Track all sent emails and SMS messages
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab label={`Email Logs (${emailTotal})`} />
            <Tab label={`SMS Logs (${smsTotal})`} />
          </Tabs>

          {/* Date Filters */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Date From"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Date To"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  disabled={!dateFrom && !dateTo}
                  fullWidth
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Email Logs Tab */}
          {tabValue === 0 && (
            <>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Recipient</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emailLogs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          {new Date(log.sent_at || log.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {log.recipient_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {log.recipient_email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{log.subject}</TableCell>
                        <TableCell>
                          <Chip 
                            label={log.email_type?.replace('_', ' ').toUpperCase() || 'EMAIL'} 
                            color="info" 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={log.status?.toUpperCase()}
                            color={log.status === 'sent' ? 'success' : 'error'}
                            size="small"
                          />
                          {log.status === 'failed' && log.error_message && (
                            <Tooltip title={log.error_message}>
                              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                Error: {log.error_message.substring(0, 30)}...
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Email Content">
                            <IconButton size="small" onClick={() => handleViewEmail(log)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {emailLogs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No email logs found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={emailTotal}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}

          {/* SMS Logs Tab */}
          {tabValue === 1 && (
            <>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Recipients</TableCell>
                      <TableCell>Message Preview</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Delivered</TableCell>
                      <TableCell>Scheduled</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {smsLogs.map((sms) => (
                      <TableRow key={sms.id} hover>
                        <TableCell>
                          {new Date(sms.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${sms.recipient_count} recipients`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                            {sms.message?.substring(0, 50)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={sms.status?.toUpperCase()}
                            color={
                              sms.status === 'sent' ? 'success' : 
                              sms.status === 'scheduled' ? 'warning' : 'error'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {sms.delivered_count}/{sms.recipient_count}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {sms.scheduled_at ? (
                            <Typography variant="body2">
                              {new Date(sms.scheduled_at).toLocaleString()}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View SMS Details">
                            <IconButton size="small" onClick={() => handleViewSms(sms)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {smsLogs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No SMS logs found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={smsTotal}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Email Content Dialog */}
      <Dialog 
        open={showEmailDialog} 
        onClose={() => setShowEmailDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>Email Details - {selectedEmail?.subject}</DialogTitle>
        <DialogContent>
          {selectedEmail && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                To: {selectedEmail.recipient_name} ({selectedEmail.recipient_email})
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Type: {selectedEmail.email_type?.replace('_', ' ').toUpperCase()}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Status: {selectedEmail.status?.toUpperCase()}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Sent: {new Date(selectedEmail.sent_at || selectedEmail.created_at).toLocaleString()}
              </Typography>
              
              {selectedEmail.error_message && (
                <Typography variant="subtitle2" color="error" gutterBottom>
                  Error: {selectedEmail.error_message}
                </Typography>
              )}
              
              <Box sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Email Content:</Typography>
                <Box 
                  dangerouslySetInnerHTML={{ __html: selectedEmail.message }}
                  sx={{ 
                    '& *': { maxWidth: '100%' },
                    '& img': { maxWidth: '100%', height: 'auto' }
                  }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* SMS Details Dialog */}
      <Dialog 
        open={showSmsDialog} 
        onClose={() => setShowSmsDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>SMS Details</DialogTitle>
        <DialogContent>
          {selectedSms && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Recipients: {selectedSms.recipient_count}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Delivered: {selectedSms.delivered_count}/{selectedSms.recipient_count}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Status: {selectedSms.status?.toUpperCase()}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Sent: {new Date(selectedSms.created_at).toLocaleString()}
              </Typography>
              {selectedSms.scheduled_at && (
                <Typography variant="subtitle2" gutterBottom>
                  Scheduled: {new Date(selectedSms.scheduled_at).toLocaleString()}
                </Typography>
              )}
              
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Message:</Typography>
                <Typography variant="body2">{selectedSms.message}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SMSLogs;